import React, { ComponentProps, useEffect, useRef, useState } from 'react';
import { asFetchableView, useRefreshView } from '../../../_lib/ViewFetcher';
import { useRefreshBlock } from '../../../_lib/BlockFetcher';
import { useArrayEffect } from '../../../_lib/components/SortableContentList/SortableContentList';
import ModalDialog from '../../../components/organisms/ModalDialog/ModalDialog';
import { BlockBar, BlockBarButton } from '../../../_lib/components/BlockBar';
import { Icons } from '../../../components/atoms/SVG';
import { Heading } from '../../../_lib/components/Heading/Heading';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { PrivilegedGroupsAddForm } from '../partials/PrivilegedGroupsAddForm';
import { SonForm } from '../../../_lib/Form2';
import { PrivilegedGroupsRolesList } from '../partials/PrivilegedGroupsRolesList';
import { PrivilegedGroupsDeleteForm } from '../partials/PrivilegedGroupsDeleteForm';
import { PrivilegedGroupsEditForm } from '../partials/PrivilegedGroupsEditForm';

type RoleId = {
  name: string;
  roleId: string;
};

export type GroupToRole = {
  edit: SonForm<any>;
  delete: SonForm<any>;
  groupId: string;
  groupName: string;
  roleIds: RoleId[];
};

type PrivilegedGroupsEditableListProps = {
  groupToRolesList: GroupToRole[];
  grantRole: SonForm<any>;
};

export function PrivilegedGroupsEdit({ groupToRolesList, grantRole }: PrivilegedGroupsEditableListProps) {
  const { t } = useSonTranslation('PrivilegedGroups');
  const ref = useRef<HTMLButtonElement>(null);

  const { refreshView } = useRefreshView();
  const { refresh } = useRefreshBlock();

  const [activeForm, setActiveForm] = useState<{
    type: 'add' | 'edit' | 'delete';
    form: SonForm<any>;
    groupToRole?: GroupToRole;
  } | null>(null);

  /**
   * After successfully changes, modal should be closed
   * */
  useArrayEffect(() => setActiveForm(null), groupToRolesList);

  /**
   * Refresh block on unmount -> keeps default view in sync with edit view
   */
  useEffect(() => {
    return () => {
      refresh();
    };
  }, [refresh]);

  const closeModal = () => setActiveForm(null);
  const openAddForm = () => setActiveForm({ type: 'add', form: grantRole });
  const openEditForm = (groupToRole: GroupToRole) =>
    setActiveForm({ type: 'edit', form: groupToRole.edit, groupToRole });
  const openDeleteForm = (form: SonForm<any>) => setActiveForm({ type: 'delete', form });

  const switchActiveForm = () => {
    switch (activeForm?.type) {
      case 'delete':
        return (
          <ModalDialog
            closeModal={closeModal}
            headline={t('removePrivilegedGroup')}
            focusOnReturn={ref}
            initialFocusIndex={1}
          >
            <PrivilegedGroupsDeleteForm form={activeForm.form} refresh={refreshView} />
          </ModalDialog>
        );
      case 'add':
        return (
          <ModalDialog closeModal={closeModal} headline={t('addPrivilegedGroup')} initialFocusIndex={1}>
            <PrivilegedGroupsAddForm form={activeForm.form} refreshView={refreshView} />
          </ModalDialog>
        );
      case 'edit':
        return (
          <ModalDialog closeModal={closeModal} headline={t('editPrivilegedGroup')} initialFocusIndex={1}>
            <PrivilegedGroupsEditForm groupToRole={activeForm.groupToRole} refreshView={refreshView} />
          </ModalDialog>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <BlockBar heading={t('addPrivilegedGroup')} hierarchy={3}>
        <BlockBarButton ref={ref} onClick={openAddForm} text={t('addPrivilegedGroup')} svgSpriteId={Icons.ADD_PLUS} />
        <Heading hierarchy={4} className="invisible">
          {t('existingPrivilegedGroups')}:
        </Heading>
      </BlockBar>
      <PrivilegedGroupsRolesList
        groupToRolesList={groupToRolesList}
        onDeleteClick={openDeleteForm}
        onEditClick={openEditForm}
      />
      {switchActiveForm()}
    </>
  );
}

export const PrivilegedGroupsEditView = asFetchableView(PrivilegedGroupsEdit);
export type PrivilegedGroupsEditView = ComponentProps<typeof PrivilegedGroupsEditView>;
